import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonDatePicker from '../../components/DatePicker/CommonDatePicker';
import { getPostById } from '../../api/boardApi';
import { getTodos, updateTodoStatus, deleteTodo } from '../../api/todoApi';
import '../../css/todo.css';
import TodoListView from './TodoListView';
import TodoListEdit from './TodoListEdit';

/* ───────── 상수 ───────── */
const DATE_FILTERS = [
  { value: 'today',    label: '오늘' },
  { value: 'upcoming', label: '예정' },
  { value: 'no_due',   label: '기한없음' },
  { value: 'overdue',  label: '기한지남' },
  { value: 'selected_date', label: '날짜지정' },
];

const STATUS_FILTERS = [
  { value: 'incomplete', label: '미완료' },
  { value: 'complete',   label: '완료' },
  { value: 'all',        label: '전체' },
];

const PRIORITY_LABEL = {
  high: '높음',
  normal: '보통',
  low: '낮음',
};

const REF_TYPE_LABEL = {
  board:    '게시판',
  approval: '전자결재',
  resource: '자원예약',
  schedule: '일정',
  pms_checklist: '프로젝트',
  pms_comment: '프로젝트',
  pms_feed: '프로젝트',
};

const REF_TYPE_BADGE = {
  board:    'bg-info text-dark',
  approval: 'bg-warning text-dark',
  resource: 'bg-success',
  schedule: 'bg-primary',
  pms_checklist: 'bg-pms',
  pms_comment: 'bg-pms',
  pms_feed: 'bg-pms',
};

const PAGE_SIZE = 15;

/* ───────── 원본 보기 URL ───────── */
function getRefUrl(refType, refId) {
  switch (refType) {
    case 'board':    return `/post/${refId}`;
    case 'approval': return `/aprvdoc/view/${refId}`;
    case 'resource': return `/reservations/${refId}`;
    case 'schedule': return `/schedule/${refId}/edit`;
    default:         return null;
  }
}

/* ─────────────────────────────── */
function TodoList() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  const splitLayoutRef = useRef(null);

  /* 목록 상태 */
  const [todos,        setTodos]        = useState([]);
  const [total,        setTotal]        = useState(0);
  const [dateFilter,   setDateFilter]   = useState('today');
  const [statusFilter, setStatusFilter] = useState('incomplete');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateInput, setSelectedDateInput] = useState(null);
  const [currentPage,  setCurrentPage]  = useState(1);

  /* 선택된 Todo 상태 */
  const [selected,   setSelected]   = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [leftWidth,  setLeftWidth]  = useState(470);
  const [isResizing, setIsResizing] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const toLocalDateText = (date) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /* ─── 목록 조회 ─── */
  const fetchTodos = useCallback(async (page = 1) => {
    if (dateFilter === 'selected_date' && !selectedDate) {
      setTodos([]);
      setTotal(0);
      return;
    }
    try {
      const res = await getTodos(
        {
          dateFilter,
          targetDate: toLocalDateText(selectedDate),
          status: statusFilter,
          page,
          limit: PAGE_SIZE,
        },
        token
      );
      setTodos(res.todos || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('[TodoList] 조회 실패:', err);
    }
  }, [dateFilter, selectedDate, statusFilter, token]);

  useEffect(() => {
    setCurrentPage(1);
    setSelected(null);
    fetchTodos(1);
  }, [dateFilter, statusFilter, selectedDate, fetchTodos]);

  useEffect(() => {
    fetchTodos(currentPage);
  }, [currentPage, fetchTodos]);

  // todos가 바뀔 때 selected가 목록에 없으면 null로 초기화, 목록이 있으면 첫 번째 항목 자동 선택
  useEffect(() => {
    if (todos.length === 0) {
      if (selected !== null) setSelected(null);
      return;
    }
    // selected가 현재 목록에 없으면 첫 번째 항목 선택
    const found = todos.find((t) => selected && t.todo_id === selected.todo_id);
    if (!found) {
      handleSelect(todos[0]);
    }
  }, [todos, selected]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const handleMouseMove = (event) => {
      if (!splitLayoutRef.current) return;
      if (window.innerWidth < 992) return;
      const bounds = splitLayoutRef.current.getBoundingClientRect();
      const nextWidth = event.clientX - bounds.left;
      const maxWidth = Math.min(600, Math.max(240, bounds.width - 320));
      const clampedWidth = Math.min(Math.max(nextWidth, 240), maxWidth);
      setLeftWidth(clampedWidth);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  /* ─── Todo 선택 ─── */
  const handleSelect = (todo) => {
    setSelected(todo);
    setIsEditMode(false);
  };

  /* ─── 상태 토글 ─── */
  const handleStatusToggle = async () => {
    const nextStatus = selected.status === 'complete' ? 'incomplete' : 'complete';
    let syncChecklist = false;
    let checklistItemToSync = null;

    if (selected.ref_type === 'pms_checklist' && selected.add_id2 && selected.ref_id) {
      try {
        const { getChecklists } = await import('../../api/pmsApi');
        const res = await getChecklists(selected.add_id2, token);
        const checklistsData = res.data || res;
        const checklistItem = Array.isArray(checklistsData) ? checklistsData.find(c => String(c.id) === String(selected.ref_id)) : null;
        
        if (checklistItem) {
          const isChecklistComplete = checklistItem.is_completed === true || checklistItem.is_completed === 1 || checklistItem.is_completed === '1';

          if (nextStatus === 'complete') {
            if (!isChecklistComplete) {
              if (window.confirm('체크리스트가 미완료 상태입니다.\n체크리스트도 완료 처리하시겠습니까?')) {
                syncChecklist = true;
                checklistItemToSync = checklistItem;
              }
            }
          } else { // nextStatus === 'incomplete'
            if (isChecklistComplete) {
              if (window.confirm('체크리스트가 완료되어 있습니다.\n체크리스트도 미완료로 처리하시겠습니까?')) {
                syncChecklist = true;
                checklistItemToSync = checklistItem;
              }
            }
          }
        }
      } catch (err) {
        console.error('[TodoList] 체크리스트 상태 확인 실패:', err);
      }
    }

    try {
      await updateTodoStatus(selected.todo_id, nextStatus, token);
      
      if (syncChecklist && checklistItemToSync) {
        try {
          const { updateChecklist } = await import('../../api/pmsApi');
          await updateChecklist(
            checklistItemToSync.id, 
            { is_completed: nextStatus === 'complete' }, 
            token
          );
        } catch (err) {
          console.error('[TodoList] 체크리스트 동기화 실패:', err);
        }
      }

      setSelected((prev) => (prev ? {
        ...prev,
        status: nextStatus,
        completed_at: nextStatus === 'complete' ? new Date().toISOString() : null,
      } : prev));
      fetchTodos(currentPage);
    } catch {
      // 상태 변경 실패 시 별도 처리 없음
    }
  };

  /* ─── 삭제 ─── */
  const handleDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteTodo(selected.todo_id, token);
      setSelected(null);
      setIsEditMode(false);
      fetchTodos(currentPage);
    } catch {
      // 삭제 실패 시 별도 처리 없음
    }
  };

  /* ─── 페이징 ─── */
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const displayTodos = todos;

  const getDueDateStyle = (todo) => {
    if (!todo.due_date || todo.due_type === 'none') return '';
    if (todo.due_date < today) return 'text-danger fw-bold';
    if (todo.due_date === today) return 'text-warning fw-bold';
    return '';
  };

  const getDueDateLabel = (todo) => {
    if (!todo.due_date || todo.due_type === 'none') return '기한없음';
    if (todo.due_date === today) return '오늘';
    return todo.due_date;
  };

  const handleResizeStart = () => {
    if (window.innerWidth < 992) return;
    setIsResizing(true);
  };

  // handleEditStart는 더 이상 사용되지 않으므로 삭제

  const handleSelectedDateSearch = () => {
    setSelectedDate(selectedDateInput);
  };

  const handleOpenOriginal = async () => {
    if (!selected) return;

    // [추가] PMS 라우팅
    if (selected.ref_type?.startsWith('pms_')) {
      if (selected.add_id1) {
        let tab = 'checklist';
        if (selected.ref_type === 'pms_comment') tab = 'comments';
        if (selected.ref_type === 'pms_feed') tab = 'comments';
        
        if (selected.add_id2) {
          // Task 서랍장
          navigate(`/pms/workspace/${selected.add_id1}?taskId=${selected.add_id2}&tab=${tab}&focusId=${selected.ref_id}`);
        } else {
          // ProjectWorkspace 메인 탭 (피드 라운지 등)
          navigate(`/pms/workspace/${selected.add_id1}?tab=${tab}&focusId=${selected.ref_id}`);
        }
      }
      return;
    }

    // robust: 일정/자원만 SPA, 나머지는 기존 방식
    if (selected.ref_type === 'schedule' && selected.ref_id) {
      navigate(`/schedule/calendar?viewId=${selected.ref_id}`);
      return;
    }
    if (selected.ref_type === 'resource' && selected.ref_id) {
      navigate(`/reservations/calendar?viewId=${selected.ref_id}`);
      return;
    }
    const baseUrl = getRefUrl(selected.ref_type, selected.ref_id);
    if (!baseUrl) return;
    if (selected.ref_type !== 'board') {
      navigate(baseUrl);
      return;
    }
    try {
      const post = await getPostById(selected.ref_id);
      if (post?.code_id) {
        navigate(`${baseUrl}?code_id=${post.code_id}`);
        return;
      }
    } catch (err) {
      console.error('[TodoList] 게시글 code_id 조회 실패:', err);
    }
    navigate(baseUrl);
  };

  /* ─── 렌더 ─── */
  return (
    <div className="container-fluid py-3 todo-list-page">
      <div className="card todo-list-shell">
        <div className="card-body">
          <div className="todo-list-header mb-3">
            <div>
              <h4 className="todo-list-title mb-1">내 To-Do 목록</h4>
              <div className="todo-list-subtitle">처리할 항목을 빠르게 확인하고 정리하세요.</div>
            </div>
            <span className="todo-list-total-pill">총 {total}건</span>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div className="todo-btn-group align-items-center">
              {DATE_FILTERS.map((f) => (
                <React.Fragment key={f.value}>
                  <button
                    className={`todo-filter-btn ${dateFilter === f.value ? 'active' : ''}`}
                    onClick={() => {
                      if (f.value === 'selected_date') {
                        // 날짜지정 클릭 시 오늘 날짜 자동 입력 및 바로 조회
                        const todayDate = new Date();
                        setSelectedDateInput(todayDate);
                        setSelectedDate(todayDate);
                        setDateFilter(f.value);
                      } else {
                        setDateFilter(f.value);
                        setSelectedDate(null);
                      }
                    }}
                    type="button"
                  >
                    {f.label}
                  </button>
                  {f.value === 'selected_date' && dateFilter === 'selected_date' && (
                    <>
                      <div className="todo-list-datepicker">
                        <CommonDatePicker
                          value={selectedDateInput}
                          onChange={setSelectedDateInput}
                          placeholder="날짜를 지정하세요."
                          inputClassName="form-control form-control-sm"
                        />
                      </div>
                      <button
                        className="btn btn-outline-secondary btn-sm todo-date-search-btn"
                        onClick={handleSelectedDateSearch}
                        type="button"
                        disabled={!selectedDateInput}
                      >
                        조회
                      </button>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="todo-btn-group">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  className={`todo-status-btn ${statusFilter === f.value ? 'active' : ''}`}
                  onClick={() => setStatusFilter(f.value)}
                  type="button"
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="todo-list-frame">
            <div className="todo-list-body" ref={splitLayoutRef}>
              <div className="todo-list-left" style={{ flexBasis: `${leftWidth}px` }}>
                <div className="todo-list-scroll-wrap">
                  <div className="todo-list-scroll">
                    {displayTodos.length === 0 ? (
                      <div className="todo-list-empty">To-Do 목록이 존재하지 않습니다.</div>
                    ) : displayTodos.map((todo) => (
                      <button
                        key={todo.todo_id}
                        type="button"
                        className={`todo-item-card ${selected?.todo_id === todo.todo_id ? 'selected' : ''}`}
                        onClick={() => handleSelect(todo)}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className={`badge ${REF_TYPE_BADGE[todo.ref_type] || 'bg-secondary'}`}>
                              {REF_TYPE_LABEL[todo.ref_type] || todo.ref_type}
                            </span>
                            {/* [추가] 프로젝트명 표시 */}
                            {todo.ref_type?.startsWith('pms_') && todo.add_title && (
                              <span className="badge bg-primary-subtle text-primary ms-1 fw-bold">
                                {todo.add_title}
                              </span>
                            )}
                          </div>
                          <span className={`badge ${todo.status === 'complete' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                            {todo.status === 'complete' ? '완료' : '미완료'}
                          </span>
                        </div>

                        <div className={`todo-item-title ${todo.status === 'complete' ? 'is-complete' : ''}`}>
                          {todo.title}
                        </div>

                        <div className="todo-item-footer d-flex justify-content-between align-items-end">
                          <span className={`todo-item-due ${getDueDateStyle(todo)}`}>
                            {getDueDateLabel(todo)}
                          </span>
                          <span className="todo-item-priority text-end" style={{ minWidth: 48, fontWeight: 600, color: todo.priority === 'high' ? '#d32f2f' : todo.priority === 'normal' ? '#1976d2' : '#757575' }}>
                            {PRIORITY_LABEL[todo.priority]}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="todo-list-pagination">
                    <nav>
                      <ul className="pagination pagination-sm mb-0 justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)} type="button">‹</button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p)} type="button">{p}</button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)} type="button">›</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>

              <div
                className={`todo-list-divider ${isResizing ? 'dragging' : ''}`}
                onMouseDown={handleResizeStart}
                role="separator"
                aria-label="목록 상세 영역 크기 조절"
                aria-orientation="vertical"
              />

              <div className="todo-list-right">
                <div className="todo-detail-scroll-wrap">
                  {isEditMode ? (
                    <TodoListEdit
                        todo={selected}
                        onUpdated={async () => {
                          setIsEditMode(false);
                          await fetchTodos(currentPage);
                          // 저장 후 DB에서 최신 데이터로 상세뷰 갱신
                          if (selected && selected.todo_id) {
                            const { getTodoById } = await import('../../api/todoApi');
                            const token = sessionStorage.getItem('token');
                            try {
                              const fresh = await getTodoById(selected.todo_id, token);
                              setSelected(fresh);
                            } catch (e) {
                              // 조회 실패 시 기존 selected 유지
                            }
                          }
                        }}
                        onCancel={() => setIsEditMode(false)}
                      />
                  ) : (
                    <TodoListView
                      selected={selected}
                      PRIORITY_LABEL={PRIORITY_LABEL}
                      getDueDateLabel={getDueDateLabel}
                      REF_TYPE_BADGE={REF_TYPE_BADGE}
                      REF_TYPE_LABEL={REF_TYPE_LABEL}
                      onEditMode={setIsEditMode}
                      onOpenOriginal={handleOpenOriginal}
                      onStatusToggle={handleStatusToggle}
                      onDelete={handleDelete}
                      isComplete={selected?.status === 'complete'}
                      loading={false}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodoList;
